/**
 * Where this device keeps its identifier and its half-finished answers.
 *
 * Split from `identity.ts` so that the decision in `resolveParticipantId` — the
 * part with branches, and the part that decides whether somebody who comes back
 * after the event is recognised — can be tested under Node without a DOM. This
 * file is the half that only makes sense inside a browser.
 */
import type { IdStore } from './identity';

const STORAGE_KEY = 'aodb.participantId';
const COOKIE_NAME = 'aodb_pid';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/**
 * `localStorage` first, a first-party cookie behind it.
 *
 * Two mechanisms rather than one because each fails in a way the other does
 * not. `localStorage` throws outright in a browser configured to block site
 * data, and Safari's storage policy can evict it; a cookie survives that but
 * is dropped by anybody clearing cookies between the two halves of an evening.
 * Writing both and reading either costs a dozen lines and turns two plausible
 * ways to lose a participant's pre-event answers into one unlikely one.
 *
 * Every access is wrapped, because in a browser with site data blocked the
 * mere act of touching `localStorage` throws, and an uncaught throw here would
 * leave the page blank rather than degraded.
 */
export function browserIdStore(): IdStore {
  return {
    read(): string | null {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored !== null) return stored;
      } catch {
        // Site data is blocked; the cookie may still be readable.
      }
      const match = new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`).exec(document.cookie);
      return match === null ? null : decodeURIComponent(match[1] as string);
    },
    write(id: string): void {
      try {
        window.localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // Nothing to do: the cookie below is the fallback this exists for.
      }
      document.cookie = [
        `${COOKIE_NAME}=${encodeURIComponent(id)}`,
        'Path=/',
        `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
        'SameSite=Lax',
        ...(location.protocol === 'https:' ? ['Secure'] : []),
      ].join('; ');
    },
  };
}

/** Answers typed but not yet sent, so that a locked phone does not lose them. */
export interface Draft {
  instrumentVersion: number;
  /** One entry per question on that form, including the optional post-only item. */
  answers: (number | null)[];
  /** Whatever is in the email field, when the form has one. */
  email?: string;
}

function draftKey(stage: string): string {
  return `aodb.draft.${stage}`;
}

/**
 * Read back a draft, discarding one written against different questions.
 *
 * Restoring positions on a scale that has since been re-worded would put
 * somebody's old answers under new statements without telling them, which is
 * worse than losing the draft.
 */
export function readDraft(stage: string, instrumentVersion: number): Draft | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(draftKey(stage));
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Draft>;
    if (parsed.instrumentVersion !== instrumentVersion) return null;
    if (!Array.isArray(parsed.answers)) return null;
    return {
      instrumentVersion,
      answers: parsed.answers.map((value) => (typeof value === 'number' ? value : null)),
      ...(typeof parsed.email === 'string' ? { email: parsed.email } : {}),
    };
  } catch {
    return null;
  }
}

export function writeDraft(stage: string, draft: Draft): void {
  try {
    window.localStorage.setItem(draftKey(stage), JSON.stringify(draft));
  } catch {
    // A draft is a convenience. Losing it is not worth interrupting anybody for.
  }
}

/**
 * Erase the draft once the answers are on the server.
 *
 * This is not housekeeping. Answers are sealed on submission — a participant
 * is not shown them again, precisely so that the second round measures what
 * they think rather than what they remember saying — and a draft left in
 * `localStorage` would be a copy of exactly that, readable from the console by
 * anybody who picked up the phone.
 */
export function clearDraft(stage: string): void {
  try {
    window.localStorage.removeItem(draftKey(stage));
  } catch {
    // Unreachable in any browser that let the draft be written in the first place.
  }
}
